<template>
  <!-- Cart Toggle Button -->
  <button
    @click="toggleCart"
    class="fixed bottom-6 right-6 bg-sonnenglas-600 hover:bg-sonnenglas-700 text-white rounded-full p-4 shadow-lg z-50 transition-all duration-200"
    :class="{ 
      'scale-110': cartStore.totalItems > 0
    }"
  >
    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
    </svg>
    
    <!-- Cart Count Badge -->
    <span 
      v-if="cartStore.totalItems > 0"
      class="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold transition-all duration-200"
    >
      {{ cartStore.totalItems }}
    </span>
  </button>

  <!-- Sidebar Overlay -->
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-hidden">
      <!-- Background overlay -->
      <div 
        @click="closeCart"
        class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
      ></div>
      
      <!-- Sidebar -->
      <div class="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
        <div class="flex flex-col h-full">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b">
            <h2 class="text-xl font-semibold text-gray-900">
              {{ $t('Warenkorb') }}
            </h2>
            <button
              @click="closeCart"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Cart Items -->
          <div class="flex-1 overflow-y-auto p-6">
            <div v-if="cartStore.items.length === 0" class="text-center py-12">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Warenkorb ist leer') }}</h3>
              <p class="mt-1 text-sm text-gray-500">{{ $t('Fügen Sie Produkte hinzu, um zu beginnen') }}</p>
            </div>

            <div v-else>
              <!-- Current Pricing Tier Display -->
              <div v-if="cartStore.currentPricingTier" class="mb-4 p-3 bg-blue-50 rounded-lg">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">{{ $t('Aktuelle Preisstufe:') }}</span>
                  <span class="font-semibold text-gray-900">{{ cartStore.currentPricingTier.name }}</span>
                </div>
                <div class="mt-1 text-xs text-gray-500">
                  {{ $t('Von') }} {{ formatPrice(cartStore.currentPricingTier.revenueThreshold) }}
                </div>
              </div>
              
              <div class="space-y-4">
                <CartItem
                  v-for="item in cartStore.items"
                  :key="item.id"
                  :item="item"
                  @update-quantity="updateQuantity"
                  @remove="removeFromCart"
                />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div v-if="cartStore.items.length > 0" class="border-t p-6">
            <!-- Total -->
            <div class="mb-4">
              <div class="flex justify-between text-lg font-semibold">
                <span>{{ $t('Zwischensumme') }}:</span>
                <span>{{ formatPrice(cartStore.totalPrice) }}</span>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ $t('zzgl. Versandkosten') }}
              </p>
            </div>

            <!-- Actions -->
            <div class="space-y-2">
              <Link
                :href="route('cart')"
                class="block w-full bg-sonnenglas-600 hover:bg-sonnenglas-700 text-white font-semibold py-3 px-4 rounded-md text-center transition-colors"
                @click="closeCart"
              >
                {{ $t('Weiter zur Kasse') }}
              </Link>
              <button
                @click="clearCart"
                class="w-full btn-secondary text-center py-2"
              >
                {{ $t('Warenkorb leeren') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useCartStore } from '@/stores/cart'
import CartItem from '@/components/CartItem.vue'

const cartStore = useCartStore()
const isOpen = ref(false)

// Inject the route function
const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

const toggleCart = () => {
  isOpen.value = !isOpen.value
}

const closeCart = () => {
  isOpen.value = false
}

const updateQuantity = (itemId: string, quantity: number) => {
  // Update Pinia store
  cartStore.updateQuantity(itemId, quantity)

  // Sync with backend
  const quantities: Record<string, number> = {}
  quantities[itemId] = quantity

  router.post(route?.('cart') || '/cart', {
    quantities
  }, {
    preserveState: true,
    preserveScroll: true,
    onError: () => {
      // On error, reload to get correct state
      router.reload()
    }
  })
}

const removeFromCart = (itemId: string) => {
  // Update Pinia store
  cartStore.removeFromCart(itemId)

  // Sync with backend by setting quantity to 0
  const quantities: Record<string, number> = {}
  quantities[itemId] = 0

  router.post(route?.('cart') || '/cart', {
    quantities
  }, {
    preserveState: true,
    preserveScroll: true,
    onError: () => {
      // On error, reload to get correct state
      router.reload()
    }
  })
}

const clearCart = () => {
  // Get all current cart items
  const quantities: Record<string, number> = {}
  cartStore.items.forEach(item => {
    quantities[item.id] = 0
  })

  // Clear Pinia store
  cartStore.clearCart()

  // Sync with backend by setting all quantities to 0
  if (Object.keys(quantities).length > 0) {
    router.post(route?.('cart') || '/cart', {
      quantities
    }, {
      preserveState: true,
      preserveScroll: true,
      onError: () => {
        // On error, reload to get correct state
        router.reload()
      }
    })
  }
}

const formatPrice = (price: number): string => {
  const currency = cartStore.currency || 'EUR'
  const locale = currency === 'CHF' ? 'de-CH' : 'de-DE'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(price)
}
</script>